import {
  distributeEpochKey,
  encodeBase64,
  encryptChannelKey,
  fetchAndCacheEpochKey,
  type EpochSyncApi,
} from '@clinikchat/shared';

/**
 * Give a member who was just added the keys the admin chose, per join (join
 * policy, 2026-10-29):
 *
 *  - includeHistory = false ("new messages only"): the channel rolls to a FRESH
 *    epoch at the join and the joiner is sealed only that key. Sharing the
 *    current key would not do: it also opens every earlier message sent under
 *    it, and a channel that never rotated would expose its whole history.
 *  - includeHistory = true: every epoch key this device can open is sealed to
 *    the joiner, so they can also read earlier messages.
 *
 * Keys are sealed on this device; the server only stores ciphertext.
 */
export type ShareMemberKeysResult =
  /** Full history: these epochs were sealed to the joiner (skipped = not held here). */
  | { status: 'shared'; epochs: number[]; skipped: number[] }
  /**
   * New messages only: the channel moved to a fresh epoch. `joinerCovered` is
   * false when the joiner has no public key yet; the boundary still holds, so a
   * later re-share gives them only the fresh epoch.
   */
  | { status: 'rolled'; joinerCovered: boolean }
  | { status: 'no-public-key' }
  | { status: 'no-current-key' }
  | { status: 'failed' };

interface EpochListRow {
  epoch: number;
  status: string;
}

/** The server accepts at most this many keys per share request. */
export const SHARE_BATCH_SIZE = 200;

interface Deps {
  api: EpochSyncApi;
  secretKey: string;
}
interface Opts {
  channelId: string;
  userId: string;
  includeHistory: boolean;
  /** The `joinedAt` the add-member call returned; binds the share to that one membership. */
  membershipJoinedAt: string;
  /** Keys per request; defaults to the server limit. Overridable so tests stay cheap. */
  batchSize?: number;
}

async function fetchJoinerPublicKey(api: EpochSyncApi, userId: string): Promise<string | null> {
  const { data } = await api.get<{ data: { publicKey: string | null } }>(
    `/api/v1/users/${userId}/public-key`,
  );
  return data.data?.publicKey ?? null;
}

async function listEpochRows(api: EpochSyncApi, channelId: string): Promise<EpochListRow[]> {
  const { data } = await api.get<{ data: EpochListRow[] }>(
    `/api/v1/channels/${channelId}/keys/epochs`,
  );
  return data.data ?? [];
}

/**
 * Give a freshly added member access to the channel's keys, as the admin chose.
 *
 * `includeHistory` off: roll the channel to a fresh epoch sealed to every member
 * (the joiner included), so they read only new messages. On: seal every finished
 * epoch to the joiner, bound to this membership via `membershipJoinedAt`. If a
 * rotation is still pending (no ACTIVE epoch yet), the finished epochs are shared
 * first and the pending epoch is then completed, so the history choice is kept.
 * Resolves with a status the caller turns into a toast; never throws.
 */
export async function shareKeysWithNewMember(
  deps: Deps,
  opts: Opts,
): Promise<ShareMemberKeysResult> {
  const { api, secretKey } = deps;
  const {
    channelId,
    userId,
    includeHistory,
    membershipJoinedAt,
    batchSize = SHARE_BATCH_SIZE,
  } = opts;

  let publicKey: string | null;
  let epochRows: EpochListRow[];
  try {
    publicKey = await fetchJoinerPublicKey(api, userId);
    epochRows = await listEpochRows(api, channelId);
  } catch {
    return { status: 'failed' };
  }

  // History shares are bound to one membership; without the token the server would
  // refuse them, so fail early with a clear status instead of posting.
  if (includeHistory && !membershipJoinedAt) return { status: 'failed' };

  if (!includeHistory)
    return rollFreshEpoch({ deps, channelId, epochRows, joinerCovered: publicKey !== null });

  if (!publicKey) return { status: 'no-public-key' };

  // Only epochs that finished distributing: a DISTRIBUTING epoch belongs to the
  // rotation flow and the server refuses it here.
  const usable = epochRows
    .filter((r) => r.status === 'ACTIVE' || r.status === 'SUPERSEDED')
    .map((r) => r.epoch)
    .sort((a, b) => b - a);
  const current = epochRows
    .filter((r) => r.status === 'ACTIVE')
    .map((r) => r.epoch)
    .sort((a, b) => b - a)[0];
  // A rotation in flight leaves no ACTIVE epoch: the previous one is SUPERSEDED and
  // its replacement DISTRIBUTING. Share what is finished, then complete the pending
  // epoch below so the member also gets the new key and the history choice survives.
  const pendingRotation =
    current === undefined && epochRows.some((r) => r.status === 'DISTRIBUTING');
  if (current === undefined && !pendingRotation) return { status: 'no-current-key' };

  const keys: { epoch: number; encryptedChannelKey: string }[] = [];
  const skipped: number[] = [];
  try {
    for (const epoch of usable) {
      const fetched = await fetchAndCacheEpochKey({ api, secretKey }, channelId, epoch);
      if (fetched.status !== 'success') {
        // The current key is the minimum: without it there is nothing useful to share.
        if (epoch === current) return { status: 'no-current-key' };
        skipped.push(epoch);
        continue;
      }
      keys.push({
        epoch,
        encryptedChannelKey: encryptChannelKey(encodeBase64(fetched.key), publicKey),
      });
    }

    // Batches of SHARE_BATCH_SIZE: one oversized request would be rejected whole
    // and leave the member with no key at all. The endpoint is insert-only, so a
    // batch that is retried later never overwrites anything.
    for (let i = 0; i < keys.length; i += batchSize) {
      await api.post(`/api/v1/channels/${channelId}/members/${userId}/keys`, {
        membershipJoinedAt,
        // The admin chose to share history: the server refuses a share that would
        // open earlier messages unless it is told so explicitly.
        allowHistory: true,
        keys: keys.slice(i, i + batchSize),
      });
    }
  } catch {
    // Includes a throw from sealing (malformed public key) as well as a rejected POST.
    return { status: 'failed' };
  }
  if (pendingRotation) {
    // Nothing finished to share means the member would get nothing useful from this path.
    if (keys.length === 0) return { status: 'no-current-key' };
    // The history is sealed (insert-only, so a retry is safe); now finish the pending
    // epoch so the joiner holds the current key too.
    const rolled = await rollFreshEpoch({ deps, channelId, epochRows, joinerCovered: true });
    if (rolled.status !== 'rolled') return { status: 'failed' };
  }
  return { status: 'shared', epochs: keys.map((k) => k.epoch), skipped };
}

/**
 * Move the channel to a fresh epoch, sealed to every current member including
 * the joiner (when they have a public key). An epoch already waiting to be
 * distributed is used as is instead of stacking a second one.
 *
 * The join is committed before this runs, so another rotation can race it. Two
 * outcomes are safe to recover from, once, with a fresh epoch list:
 *  - the server refused the distribution because the member list this device
 *    fetched was already stale (it rejects any distribution that omits a current
 *    member with a key bundle), or
 *  - another device won the epoch number first (409), and that epoch may have
 *    been sealed before the joiner existed.
 * A second failure is reported as `failed` so the admin sees the warning toast.
 */
async function rollFreshEpoch({
  deps,
  channelId,
  epochRows,
  joinerCovered,
}: {
  deps: Deps;
  channelId: string;
  epochRows: EpochListRow[];
  joinerCovered: boolean;
}): Promise<ShareMemberKeysResult> {
  let rows = epochRows;
  for (let attempt = 0; attempt < 2; attempt++) {
    const pending = rows.find((r) => r.status === 'DISTRIBUTING');
    const highest = rows.reduce((max, r) => Math.max(max, r.epoch), 0);
    const target = pending ? pending.epoch : highest + 1;

    const result = await distributeEpochKey(deps, channelId, target, { ignoreRefusals: true });
    if (result.ok) return { status: 'rolled', joinerCovered };
    if (result.reason === 'nobody' || result.reason === 'dropped') break;
    if (attempt === 1) break;
    try {
      rows = await listEpochRows(deps.api, channelId);
    } catch {
      break;
    }
  }
  return { status: 'failed' };
}
